<template>
  <div
    class="cdb-widget-skeleton"
    :class="[
      `cdb-widget-skeleton--${family}`,
      `cdb-widget-skeleton--${normalizedType}`,
      {
        'cdb-widget-skeleton--builder': builderHeader,
        'cdb-widget-skeleton--table-body-only': tableBodyOnly
      }
    ]"
    role="status"
    aria-live="polite"
    aria-busy="true">
    <span class="cdb-widget-skeleton__sr-only">{{ loadingLabel }}</span>

    <div v-if="rendersStandardHeader" class="cdb-widget-skeleton__head">
      <div class="cdb-widget-skeleton__head-copy">
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__title"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__subtitle"></span>
      </div>
      <span v-if="builderHeader" class="cdb-widget-skeleton__block cdb-widget-skeleton__badge"></span>
    </div>

    <div v-if="family === 'kpi'" class="cdb-widget-skeleton__kpi">
      <div v-if="builderHeader" class="cdb-widget-skeleton__kpi-top">
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__kpi-label"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__badge"></span>
      </div>
      <div class="cdb-widget-skeleton__kpi-value">
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__metric"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__unit"></span>
      </div>
      <div v-if="builderHeader" class="cdb-widget-skeleton__kpi-meta">
        <span class="cdb-widget-skeleton__block"></span>
      </div>
    </div>

    <div v-else-if="family === 'table'" class="cdb-widget-skeleton__table-shell">
      <div v-if="rendersTableToolbar" class="cdb-widget-skeleton__toolbar">
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__search"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__action"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__action cdb-widget-skeleton__action--wide"></span>
      </div>
      <div v-if="normalizedType === 'datagrid' && showTableToolbar" class="cdb-widget-skeleton__scope">
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__scope-icon"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__scope-label"></span>
        <span class="cdb-widget-skeleton__block cdb-widget-skeleton__scope-value"></span>
      </div>
      <div class="cdb-widget-skeleton__table" aria-hidden="true">
        <div v-if="!tableBodyOnly" class="cdb-widget-skeleton__table-row cdb-widget-skeleton__table-row--head">
          <span
            v-for="(width, columnIndex) in tableColumnWidths"
            :key="`head-${columnIndex}`"
            class="cdb-widget-skeleton__table-cell"
            :style="{ flex: width }">
            <i class="cdb-widget-skeleton__block" :style="{ width: headerLineWidths[columnIndex] }"></i>
          </span>
        </div>
        <div
          v-for="rowIndex in tableRows"
          :key="`row-${rowIndex}`"
          class="cdb-widget-skeleton__table-row">
          <span
            v-for="(width, columnIndex) in tableColumnWidths"
            :key="`cell-${rowIndex}-${columnIndex}`"
            class="cdb-widget-skeleton__table-cell"
            :style="{ flex: width }">
            <i
              class="cdb-widget-skeleton__block"
              :style="{ width: cellLineWidth(rowIndex, columnIndex) }"></i>
          </span>
        </div>
        <div v-if="normalizedType === 'datagrid' && !tableBodyOnly" class="cdb-widget-skeleton__pager">
          <span class="cdb-widget-skeleton__block"></span>
          <span class="cdb-widget-skeleton__block"></span>
        </div>
      </div>
    </div>

    <div v-else-if="family === 'circular'" class="cdb-widget-skeleton__chart cdb-widget-skeleton__chart--circular">
      <div class="cdb-widget-skeleton__ring" aria-hidden="true"></div>
      <div class="cdb-widget-skeleton__legend">
        <div v-for="index in 4" :key="`legend-${index}`" class="cdb-widget-skeleton__legend-row">
          <span class="cdb-widget-skeleton__block cdb-widget-skeleton__legend-dot"></span>
          <span class="cdb-widget-skeleton__block" :style="{ width: legendLineWidths[index - 1] }"></span>
        </div>
      </div>
    </div>

    <div v-else class="cdb-widget-skeleton__chart">
      <div v-if="normalizedType === 'bar'" class="cdb-widget-skeleton__bar-chart" aria-hidden="true">
        <div v-for="(width, index) in barWidths" :key="`bar-${index}`" class="cdb-widget-skeleton__bar-row">
          <span class="cdb-widget-skeleton__block cdb-widget-skeleton__axis-label"></span>
          <span class="cdb-widget-skeleton__block cdb-widget-skeleton__bar" :style="{ width }"></span>
        </div>
      </div>

      <div v-else-if="normalizedType === 'column'" class="cdb-widget-skeleton__column-chart" aria-hidden="true">
        <span
          v-for="(height, index) in columnHeights"
          :key="`column-${index}`"
          class="cdb-widget-skeleton__block cdb-widget-skeleton__column"
          :style="{ height }"></span>
      </div>

      <div v-else class="cdb-widget-skeleton__plot" aria-hidden="true">
        <span v-for="index in 4" :key="`grid-${index}`" class="cdb-widget-skeleton__grid-line"></span>
        <svg viewBox="0 0 100 52" preserveAspectRatio="none" focusable="false">
          <path
            v-if="normalizedType === 'area'"
            class="cdb-widget-skeleton__area"
            d="M2,46 L18,31 L34,37 L51,19 L67,25 L82,12 L98,18 L98,52 L2,52 Z" />
          <path
            class="cdb-widget-skeleton__line"
            d="M2,46 L18,31 L34,37 L51,19 L67,25 L82,12 L98,18" />
        </svg>
      </div>

      <div class="cdb-widget-skeleton__chart-legend" aria-hidden="true">
        <span class="cdb-widget-skeleton__block"></span>
        <span class="cdb-widget-skeleton__block"></span>
        <span class="cdb-widget-skeleton__block"></span>
      </div>
    </div>
  </div>
</template>

<script>
const TABLE_TYPES = Object.freeze(['datagrid', 'table']);
const CIRCULAR_TYPES = Object.freeze(['pie', 'donut']);
const CHART_TYPES = Object.freeze(['bar', 'column', 'line', 'area']);
const SUPPORTED_TYPES = Object.freeze([...TABLE_TYPES, 'count', ...CIRCULAR_TYPES, ...CHART_TYPES]);

export default {
  name: 'DashboardWidgetSkeleton',

  props: {
    widgetType: {
      type: String,
      default: 'bar',
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    builderHeader: {
      type: Boolean,
      default: false,
    },
    showTableToolbar: {
      type: Boolean,
      default: true,
    },
    /** Render only placeholder data rows. Used inside a live grid so its persistent
     *  search, scope, column headers, and pagination remain visible and interactive. */
    tableBodyOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tableRows: 5,
      tableColumnWidths: [0.72, 1.35, 1, 1.12, 1.42],
      headerLineWidths: ['52%', '74%', '62%', '68%', '57%'],
      legendLineWidths: ['68%', '82%', '58%', '74%'],
      barWidths: ['78%', '54%', '88%', '66%', '42%'],
      columnHeights: ['42%', '68%', '84%', '55%', '74%', '48%'],
    };
  },

  computed: {
    normalizedType() {
      const candidate = String(this.widgetType || '').toLowerCase();
      return SUPPORTED_TYPES.includes(candidate) ? candidate : 'bar';
    },
    family() {
      if (TABLE_TYPES.includes(this.normalizedType)) return 'table';
      if (this.normalizedType === 'count') return 'kpi';
      if (CIRCULAR_TYPES.includes(this.normalizedType)) return 'circular';
      return 'chart';
    },
    rendersStandardHeader() {
      return this.showHeader && !(this.family === 'kpi' && this.builderHeader);
    },
    rendersTableToolbar() {
      return this.normalizedType === 'datagrid' && this.showTableToolbar;
    },
    loadingLabel() {
      const labels = {
        datagrid: 'data grid',
        table: 'table',
        count: 'KPI',
        bar: 'bar chart',
        column: 'column chart',
        line: 'line chart',
        area: 'area chart',
        pie: 'pie chart',
        donut: 'donut chart',
      };
      return `Loading ${labels[this.normalizedType]} widget`;
    },
  },

  methods: {
    cellLineWidth(rowIndex, columnIndex) {
      const widths = ['78%', '61%', '86%', '54%', '70%', '46%'];
      return widths[(rowIndex + columnIndex * 2) % widths.length];
    },
  },
};
</script>

<style lang="scss" scoped>
.cdb-widget-skeleton {
  --cdb-skeleton-base: rgba(41, 49, 46, 0.10);
  --cdb-skeleton-highlight: rgba(252, 253, 252, 0.72);
  --cdb-skeleton-border: var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  --cdb-skeleton-surface: var(--ds-surface-elevated, #fcfdfc);

  container-type: size;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  color: var(--v-text-base);
  font-family: var(--ds-font-family-body, 'IBM Plex Sans', sans-serif);
}

.theme--dark .cdb-widget-skeleton {
  --cdb-skeleton-base: rgba(255, 255, 255, 0.09);
  --cdb-skeleton-highlight: rgba(255, 255, 255, 0.14);
}

.cdb-widget-skeleton__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.cdb-widget-skeleton__block {
  position: relative;
  display: block;
  min-width: 8px;
  min-height: 8px;
  overflow: hidden;
  background: var(--cdb-skeleton-base);
  border-radius: var(--ds-radius-sm, 4px);
}

.cdb-widget-skeleton__block::after,
.cdb-widget-skeleton__ring::after {
  position: absolute;
  inset: 0;
  content: '';
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--cdb-skeleton-highlight) 46%,
    transparent 82%
  );
  transform: translateX(-110%);
  animation: cdb-skeleton-shimmer 1.65s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

.cdb-widget-skeleton__head {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-height: 45px;
  padding: 3px 8px 8px;
}

.cdb-widget-skeleton--datagrid .cdb-widget-skeleton__head {
  min-height: 63px;
}

.cdb-widget-skeleton__head-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.cdb-widget-skeleton__title {
  width: clamp(108px, 38%, 210px);
  height: 15px;
}

.cdb-widget-skeleton--datagrid .cdb-widget-skeleton__title {
  height: 19px;
}

.cdb-widget-skeleton__subtitle {
  width: clamp(145px, 56%, 310px);
  height: 10px;
}

.cdb-widget-skeleton__badge {
  flex: 0 0 auto;
  width: 37px;
  height: 16px;
  border-radius: var(--ds-radius-full, 9999px);
}

/* KPI skeleton mirrors the full-height label, metric, and bottom-anchored metadata anatomy. */
.cdb-widget-skeleton__kpi {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 3px 5px 2px;
}

.cdb-widget-skeleton__kpi-top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 16px;
}

.cdb-widget-skeleton__kpi-label {
  width: clamp(78px, 34%, 150px);
  height: 10px;
}

.cdb-widget-skeleton__kpi-value {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-top: 11px;
}

.cdb-widget-skeleton__metric {
  width: clamp(78px, 34%, 148px);
  height: clamp(30px, 30cqh, 42px);
  border-radius: 6px;
}

.cdb-widget-skeleton__unit {
  width: 29px;
  height: 14px;
  margin-bottom: 3px;
}

.cdb-widget-skeleton__kpi-meta {
  margin-top: auto;
  padding-top: 9px;
  border-top: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__kpi-meta .cdb-widget-skeleton__block {
  width: min(82%, 260px);
  height: 10px;
}

/* Datagrid and table retain the same toolbar, scope strip, columns, rows, and pager rhythm. */
.cdb-widget-skeleton__table-shell {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--cdb-skeleton-border);
  border-radius: 6px;
}

.cdb-widget-skeleton--table-body-only .cdb-widget-skeleton__table-shell {
  border: 0;
  border-radius: 0;
}

.cdb-widget-skeleton__toolbar {
  display: flex;
  flex: 0 0 48px;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-bottom: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__search {
  flex: 1 1 auto;
  height: 34px;
  background: transparent;
  border: 1px solid var(--cdb-skeleton-base);
}

.cdb-widget-skeleton__search::before {
  position: absolute;
  top: 9px;
  left: 12px;
  width: 13px;
  height: 13px;
  content: '';
  border: 2px solid var(--cdb-skeleton-base);
  border-radius: 50%;
}

.cdb-widget-skeleton__search::after {
  background: linear-gradient(90deg, transparent 0%, var(--cdb-skeleton-highlight) 46%, transparent 82%);
}

.cdb-widget-skeleton__action {
  flex: 0 0 38px;
  height: 34px;
}

.cdb-widget-skeleton__action--wide {
  flex-basis: 88px;
}

.cdb-widget-skeleton__scope {
  display: flex;
  flex: 0 0 39px;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--ds-surface-hover, rgba(0, 0, 0, 0.04));
  border-bottom: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__scope-icon {
  width: 15px;
  height: 15px;
}

.cdb-widget-skeleton__scope-label {
  width: 78px;
  height: 9px;
}

.cdb-widget-skeleton__scope-value {
  width: min(28%, 180px);
  height: 9px;
}

.cdb-widget-skeleton__table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.cdb-widget-skeleton__table-row {
  display: flex;
  flex: 1 1 0;
  min-height: 25px;
  border-bottom: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__table-row--head {
  flex: 0 0 40px;
  background: var(--ds-surface-hover, rgba(0, 0, 0, 0.04));
}

.cdb-widget-skeleton__table-cell {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0 10px;
  border-right: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__table-cell:last-child {
  border-right: 0;
}

.cdb-widget-skeleton__table-cell .cdb-widget-skeleton__block {
  height: 10px;
}

.cdb-widget-skeleton__table-row--head .cdb-widget-skeleton__block {
  height: 11px;
}

.cdb-widget-skeleton__pager {
  display: flex;
  flex: 0 0 34px;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
  padding: 0 12px;
}

.cdb-widget-skeleton__pager .cdb-widget-skeleton__block:first-child {
  width: 62px;
  height: 9px;
}

.cdb-widget-skeleton__pager .cdb-widget-skeleton__block:last-child {
  width: 45px;
  height: 9px;
}

/* Chart skeletons use the same plot-to-legend proportions as the rendered widget. */
.cdb-widget-skeleton__chart {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 4px 10px 2px;
}

.cdb-widget-skeleton__bar-chart {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-evenly;
  min-height: 0;
  padding: 4px 8px 2px;
  border-bottom: 1px solid var(--cdb-skeleton-border);
  border-left: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__bar-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.cdb-widget-skeleton__axis-label {
  flex: 0 0 clamp(30px, 14%, 74px);
  height: 8px;
}

.cdb-widget-skeleton__bar {
  height: clamp(11px, 12cqh, 22px);
  border-radius: 3px;
}

.cdb-widget-skeleton__column-chart {
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;
  justify-content: space-evenly;
  gap: clamp(5px, 2.5%, 15px);
  min-height: 0;
  padding: 12px 10px 0;
  border-bottom: 1px solid var(--cdb-skeleton-border);
  border-left: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__column {
  flex: 1 1 0;
  max-width: 46px;
  min-height: 16px;
  border-radius: 4px 4px 0 0;
}

.cdb-widget-skeleton__plot {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border-bottom: 1px solid var(--cdb-skeleton-border);
  border-left: 1px solid var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__grid-line {
  position: absolute;
  right: 0;
  left: 0;
  height: 1px;
  background: var(--cdb-skeleton-border);
}

.cdb-widget-skeleton__grid-line:nth-child(1) { top: 20%; }
.cdb-widget-skeleton__grid-line:nth-child(2) { top: 40%; }
.cdb-widget-skeleton__grid-line:nth-child(3) { top: 60%; }
.cdb-widget-skeleton__grid-line:nth-child(4) { top: 80%; }

.cdb-widget-skeleton__plot svg {
  position: absolute;
  inset: 10% 3% 4% 3%;
  width: 94%;
  height: 86%;
}

.cdb-widget-skeleton__line {
  fill: none;
  stroke: var(--cdb-skeleton-base);
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.cdb-widget-skeleton__area {
  fill: var(--cdb-skeleton-base);
  opacity: 0.72;
}

.cdb-widget-skeleton__chart-legend {
  display: flex;
  flex: 0 0 24px;
  align-items: flex-end;
  justify-content: center;
  gap: 14px;
}

.cdb-widget-skeleton__chart-legend .cdb-widget-skeleton__block {
  width: clamp(36px, 16%, 72px);
  height: 8px;
}

.cdb-widget-skeleton__chart--circular {
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: clamp(18px, 8%, 48px);
}

.cdb-widget-skeleton__ring {
  position: relative;
  flex: 0 1 auto;
  width: min(47cqh, 42cqw, 180px);
  aspect-ratio: 1;
  overflow: hidden;
  background: var(--cdb-skeleton-base);
  border-radius: 50%;
}

.cdb-widget-skeleton__ring::before {
  position: absolute;
  z-index: 2;
  inset: 24%;
  content: '';
  background: var(--cdb-skeleton-surface);
  border-radius: 50%;
}

.cdb-widget-skeleton__legend {
  display: flex;
  flex: 0 1 34%;
  flex-direction: column;
  gap: 11px;
  min-width: 96px;
  max-width: 190px;
}

.cdb-widget-skeleton__legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cdb-widget-skeleton__legend-row > .cdb-widget-skeleton__block:last-child {
  height: 9px;
}

.cdb-widget-skeleton__legend-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

@container (max-width: 320px) {
  .cdb-widget-skeleton__chart--circular {
    flex-direction: column;
    gap: 12px;
  }

  .cdb-widget-skeleton__ring {
    width: min(42cqh, 52cqw, 130px);
  }

  .cdb-widget-skeleton__legend {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 88%;
    max-width: none;
  }

  .cdb-widget-skeleton__action--wide,
  .cdb-widget-skeleton__table-cell:nth-child(n + 4) {
    display: none;
  }
}

@container (max-height: 150px) {
  .cdb-widget-skeleton__subtitle,
  .cdb-widget-skeleton__chart-legend,
  .cdb-widget-skeleton__table-row:nth-of-type(n + 5) {
    display: none;
  }

  .cdb-widget-skeleton__head {
    min-height: 32px;
    padding-bottom: 5px;
  }

  .cdb-widget-skeleton__kpi-value {
    margin-top: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cdb-widget-skeleton__block::after,
  .cdb-widget-skeleton__ring::after {
    animation: none;
    opacity: 0.35;
    transform: none;
  }
}

@keyframes cdb-skeleton-shimmer {
  to { transform: translateX(110%); }
}
</style>
